import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface KeepCustomer {
  id: string;
  name: string | null;
  reason: string;
  divus_contact_id: string | null;
}

export interface MergeFromCustomer {
  id: string;
  name: string | null;
  action: 'archive' | 'delete';
  reason: string;
}

export interface DuplicateGroup {
  email: string;
  keep: KeepCustomer;
  merge_from: MergeFromCustomer[];
  merge_actions: string[];
}

export interface CustomersBreakdown {
  total_from_api: number;
  deleted: number;
  active: number;
  with_email: number;
  without_email: number;
  guests: number;
  guests_with_email: number;
  guests_without_email: number;
  real_customers: number;
  real_with_email: number;
  real_without_email: number;
}

export interface CleanupSummary {
  totalCustomers: number;
  emailsAnalyzed: number;
  duplicatesFound: number;
  toMerge: number;
  toArchive: number;
  toDelete: number;
}

export interface GuestMatch {
  guest: {
    id: string;
    email: string | null;
    name: string | null;
    charges_count: number;
  };
  possible_owner: {
    id: string;
    email: string | null;
    name: string;
    match_score: number;
    matched_parts: string[];
  };
  suggested_action: string;
}

export interface GuestSummary {
  total_guests_with_email: number;
  total_real_customers: number;
  total_matched: number;
  total_unmatched: number;
}

interface BatchResponse {
  mode: string;
  dry_run: boolean;
  batch: {
    offset: number;
    batch_size: number;
    processed: number;
    total_customers: number;
    has_more: boolean;
    next_offset: number | null;
  };
  customers_breakdown?: CustomersBreakdown;
  duplicates?: DuplicateGroup[];
  summary: {
    emails_analyzed?: number;
    duplicates_found?: number;
    to_merge?: number;
    to_archive?: number;
    to_delete?: number;
  };
  errors: { stage: string; message: string; customerId?: string }[];
}

interface GuestMatchResponse {
  mode: string;
  dry_run: boolean;
  batch: {
    offset: number;
    batch_size: number;
    processed: number;
    total_guests: number;
    has_more: boolean;
    next_offset: number | null;
  };
  potential_matches: GuestMatch[];
  unmatched_guests: { id: string; email: string | null; name: string | null }[];
  summary: GuestSummary;
  customers_breakdown: CustomersBreakdown;
}

interface ExecutionResult {
  success: boolean;
  email: string;
  error?: string;
}

export function useStripeCleanup() {
  // Duplicates state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [summary, setSummary] = useState<CleanupSummary | null>(null);
  const [executionResults, setExecutionResults] = useState<ExecutionResult[]>([]);
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [customersBreakdown, setCustomersBreakdown] = useState<CustomersBreakdown | null>(null);

  // Guest matching state
  const [isAnalyzingGuests, setIsAnalyzingGuests] = useState(false);
  const [isExecutingGuests, setIsExecutingGuests] = useState(false);
  const [guestProgress, setGuestProgress] = useState(0);
  const [guestProgressText, setGuestProgressText] = useState('');
  const [guestMatches, setGuestMatches] = useState<GuestMatch[]>([]);
  const [unmatchedGuests, setUnmatchedGuests] = useState<{ id: string; email: string | null; name: string | null }[]>([]);
  const [guestSummary, setGuestSummary] = useState<GuestSummary | null>(null);
  const [selectedGuestIds, setSelectedGuestIds] = useState<Set<string>>(new Set());

  const analyzeAll = useCallback(async () => {
    setIsAnalyzing(true);
    setProgress(0);
    setProgressText('Iniciando análise...');
    setDuplicates([]);
    setSummary(null);
    setExecutionResults([]);
    setCustomersBreakdown(null);

    try {
      let offset = 0;
      let allDuplicates: DuplicateGroup[] = [];
      let hasMore = true;
      let totalCustomers = 0;
      let breakdown: CustomersBreakdown | null = null;
      let totalSummary = {
        emailsAnalyzed: 0,
        duplicatesFound: 0,
        toMerge: 0,
        toArchive: 0,
        toDelete: 0,
      };

      while (hasMore) {
        setProgressText(`Analisando... ${offset} customers processados`);

        const { data, error } = await supabase.functions.invoke<BatchResponse>(
          'stripe-cleanup-duplicates',
          {
            body: { mode: 'find_duplicates', dry_run: true, batch_size: 50, offset },
          }
        );

        if (error) {
          throw new Error(error.message || 'Erro ao analisar duplicados');
        }

        if (!data) {
          throw new Error('Resposta vazia do servidor');
        }

        // Capture breakdown from first response
        if (!breakdown && data.customers_breakdown) {
          breakdown = data.customers_breakdown;
          setCustomersBreakdown(breakdown);
        }

        // Accumulate duplicates
        if (data.duplicates && data.duplicates.length > 0) {
          allDuplicates = [...allDuplicates, ...data.duplicates];
        }

        // Update totals
        totalCustomers = data.batch.total_customers;
        totalSummary.emailsAnalyzed += data.summary.emails_analyzed || 0;
        totalSummary.duplicatesFound += data.summary.duplicates_found || 0;
        totalSummary.toMerge += data.summary.to_merge || 0;
        totalSummary.toArchive += data.summary.to_archive || 0;
        totalSummary.toDelete += data.summary.to_delete || 0;

        // Update progress
        hasMore = data.batch.has_more;
        offset = data.batch.next_offset || offset + 50;
        setProgress((offset / totalCustomers) * 100);
        setProgressText(`Analisando... ${Math.min(offset, totalCustomers)}/${totalCustomers} customers`);

        // Log errors if any
        if (data.errors && data.errors.length > 0) {
          console.warn('Batch errors:', data.errors);
        }
      }

      // Merge duplicates with same email from different batches
      const mergedDuplicates = mergeDuplicatesByEmail(allDuplicates);

      setDuplicates(mergedDuplicates);
      setSummary({
        totalCustomers,
        ...totalSummary,
      });
      setProgress(100);
      setProgressText('Análise concluída!');
      toast.success(`Análise concluída! ${mergedDuplicates.length} grupos de duplicados encontrados.`);
    } catch (error) {
      console.error('Error analyzing duplicates:', error);
      toast.error(`Erro ao analisar: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      setProgressText('Erro na análise');
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  const analyzeGuests = useCallback(async () => {
    setIsAnalyzingGuests(true);
    setGuestProgress(0);
    setGuestProgressText('Iniciando análise de guests...');
    setGuestMatches([]);
    setUnmatchedGuests([]);
    setGuestSummary(null);
    setSelectedGuestIds(new Set());

    try {
      let offset = 0;
      let allMatches: GuestMatch[] = [];
      let allUnmatched: { id: string; email: string | null; name: string | null }[] = [];
      let hasMore = true;
      let totalGuests = 0;
      let finalSummary: GuestSummary | null = null;

      while (hasMore) {
        setGuestProgressText(`Analisando guests... ${offset} processados`);

        const { data, error } = await supabase.functions.invoke<GuestMatchResponse>(
          'stripe-cleanup-duplicates',
          {
            body: { mode: 'match_guests_by_name', dry_run: true, batch_size: 50, offset },
          }
        );

        if (error) {
          throw new Error(error.message || 'Erro ao analisar guests');
        }

        if (!data) {
          throw new Error('Resposta vazia do servidor');
        }

        // Capture breakdown if not set
        if (!customersBreakdown && data.customers_breakdown) {
          setCustomersBreakdown(data.customers_breakdown);
        }

        // Accumulate results
        allMatches = [...allMatches, ...data.potential_matches];
        allUnmatched = [...allUnmatched, ...data.unmatched_guests];

        // Update totals
        totalGuests = data.batch.total_guests;
        finalSummary = data.summary;

        // Update progress
        hasMore = data.batch.has_more;
        offset = data.batch.next_offset || offset + 50;
        setGuestProgress((offset / totalGuests) * 100);
        setGuestProgressText(`Analisando guests... ${Math.min(offset, totalGuests)}/${totalGuests}`);
      }

      setGuestMatches(allMatches);
      setUnmatchedGuests(allUnmatched);
      setGuestSummary(finalSummary);
      setGuestProgress(100);
      setGuestProgressText('Análise concluída!');
      toast.success(`Análise concluída! ${allMatches.length} possíveis matches encontrados.`);
    } catch (error) {
      console.error('Error analyzing guests:', error);
      toast.error(`Erro ao analisar guests: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      setGuestProgressText('Erro na análise');
    } finally {
      setIsAnalyzingGuests(false);
    }
  }, [customersBreakdown]);

  const executeMerge = useCallback(async (emails: string[]) => {
    if (emails.length === 0) {
      toast.error('Nenhum duplicado selecionado');
      return;
    }

    setIsExecuting(true);
    setExecutionResults([]);
    const results: ExecutionResult[] = [];

    try {
      for (const email of emails) {
        try {
          const group = duplicates.find(d => d.email === email);
          if (!group) continue;

          const { data, error } = await supabase.functions.invoke(
            'stripe-cleanup-duplicates',
            {
              body: { 
                mode: 'find_duplicates',
                dry_run: false, 
                execute_emails: [email],
                batch_size: 1,
                offset: 0
              },
            }
          );

          if (error) {
            results.push({ success: false, email, error: error.message });
          } else {
            results.push({ success: true, email });
          }
        } catch (e) {
          results.push({ 
            success: false, 
            email, 
            error: e instanceof Error ? e.message : 'Erro desconhecido' 
          });
        }
      }

      setExecutionResults(results);
      
      const successCount = results.filter(r => r.success).length;
      const errorCount = results.filter(r => !r.success).length;
      
      if (errorCount === 0) {
        toast.success(`${successCount} grupos processados com sucesso!`);
      } else {
        toast.warning(`${successCount} sucesso, ${errorCount} erros`);
      }

      await analyzeAll();
    } catch (error) {
      console.error('Error executing merge:', error);
      toast.error(`Erro ao executar: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setIsExecuting(false);
    }
  }, [duplicates, analyzeAll]);

  const toggleSelection = useCallback((email: string) => {
    setSelectedEmails(prev => {
      const newSet = new Set(prev);
      if (newSet.has(email)) {
        newSet.delete(email);
      } else {
        newSet.add(email);
      }
      return newSet;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedEmails(new Set(duplicates.map(d => d.email)));
  }, [duplicates]);

  const clearSelection = useCallback(() => {
    setSelectedEmails(new Set());
  }, []);

  // Guest selection
  const toggleGuestSelection = useCallback((guestId: string) => {
    setSelectedGuestIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(guestId)) {
        newSet.delete(guestId);
      } else {
        newSet.add(guestId);
      }
      return newSet;
    });
  }, []);

  const selectAllGuests = useCallback(() => {
    setSelectedGuestIds(new Set(guestMatches.map(m => m.guest.id)));
  }, [guestMatches]);

  const clearGuestSelection = useCallback(() => {
    setSelectedGuestIds(new Set());
  }, []);

  const executeGuestMerge = useCallback(async (guestIds: string[]) => {
    if (guestIds.length === 0) {
      toast.error('Nenhum guest selecionado');
      return;
    }

    setIsExecutingGuests(true);
    const results: { success: boolean; guestId: string; error?: string }[] = [];

    try {
      for (const guestId of guestIds) {
        const match = guestMatches.find(m => m.guest.id === guestId);
        if (!match) {
          results.push({ success: false, guestId, error: 'Match not found' });
          continue;
        }

        try {
          const { data, error } = await supabase.functions.invoke(
            'stripe-cleanup-duplicates',
            {
              body: { 
                mode: 'execute_guest_merge',
                dry_run: false,
                guest_id: match.guest.id,
                owner_id: match.possible_owner.id,
              },
            }
          );

          if (error) {
            results.push({ success: false, guestId, error: error.message });
          } else if (data?.success) {
            results.push({ success: true, guestId });
          } else {
            results.push({ success: false, guestId, error: 'Unknown error' });
          }
        } catch (e) {
          results.push({ 
            success: false, 
            guestId, 
            error: e instanceof Error ? e.message : 'Erro desconhecido' 
          });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const errorCount = results.filter(r => !r.success).length;

      if (errorCount === 0) {
        toast.success(`${successCount} guests merged com sucesso!`);
      } else {
        toast.warning(`${successCount} sucesso, ${errorCount} erros`);
      }

      // Re-analyze to update the list
      await analyzeGuests();
    } catch (error) {
      console.error('Error executing guest merge:', error);
      toast.error(`Erro ao executar merge: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    } finally {
      setIsExecutingGuests(false);
    }
  }, [guestMatches, analyzeGuests]);

  const exportReport = useCallback(() => {
    const report = {
      generatedAt: new Date().toISOString(),
      customersBreakdown,
      summary,
      duplicates: duplicates.map(d => ({
        email: d.email,
        keep: d.keep,
        merge_from: d.merge_from,
        merge_actions: d.merge_actions,
      })),
      guestMatches,
      unmatchedGuests,
      guestSummary,
      executionResults,
    };

    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stripe-cleanup-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [summary, duplicates, executionResults, customersBreakdown, guestMatches, unmatchedGuests, guestSummary]);

  return {
    // Duplicates state
    isAnalyzing,
    isExecuting,
    progress,
    progressText,
    duplicates,
    summary,
    executionResults,
    selectedEmails,
    customersBreakdown,
    
    // Guest matching state
    isAnalyzingGuests,
    isExecutingGuests,
    guestProgress,
    guestProgressText,
    guestMatches,
    unmatchedGuests,
    guestSummary,
    selectedGuestIds,
    
    // Duplicates actions
    analyzeAll,
    executeMerge,
    toggleSelection,
    selectAll,
    clearSelection,
    
    // Guest actions
    analyzeGuests,
    executeGuestMerge,
    toggleGuestSelection,
    selectAllGuests,
    clearGuestSelection,
    
    // Common
    exportReport,
  };
}

// Helper to merge duplicates with same email from different batches
function mergeDuplicatesByEmail(duplicates: DuplicateGroup[]): DuplicateGroup[] {
  const emailMap = new Map<string, DuplicateGroup>();

  for (const dup of duplicates) {
    const existing = emailMap.get(dup.email);
    if (existing) {
      existing.merge_from = [...existing.merge_from, ...dup.merge_from];
      existing.merge_actions = [...existing.merge_actions, ...dup.merge_actions];
    } else {
      emailMap.set(dup.email, { ...dup });
    }
  }

  return Array.from(emailMap.values());
}
